import { PaginationPageDefault, ToastCustom } from "@components";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import { useInfractionsSignIn } from "@features/infractions/hooks/use-infractions-sign-in";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { findPaginationFocusTarget } from "@/shared/components/pagination/pagination-base";
import {
	InfractionCreateDrawer,
	InfractionDetailDrawer,
	InfractionOnboardingModal,
	InfractionSuccessModal,
	InfractionsEmptyState,
	InfractionsExportModal,
	InfractionsFiltersToolbar,
	InfractionsMetricsCards,
	InfractionsPageHeader,
	InfractionsTable,
} from "../components";
import { INFRACTION_METRIC_FILTER } from "../constants/infraction-metrics.constant";
import { INFRACTION_LIST_PAGE_SIZE } from "../constants/infraction-table-columns.constant";
import {
	buildInfractionPaginatedRequest,
	hasActiveInfractionFilters,
} from "../handlers/build-infraction-list-params";
import {
	invalidateInfractionDetailQueries,
	invalidateInfractionListQueries,
	useHabilitarMeStatus,
	useInfractionsPaginated,
	useInfractionsSummary,
	useMarkInfractionOnboardingSeen,
	useUserProfile,
} from "../query";
import { useInfractionModuleFooterStore } from "../store/infraction-module-footer.store";
import type {
	InfractionListFilters,
	InfractionMetricFilter,
	InfractionMetricsSummary,
} from "../types/infraction.types";

const DEFAULT_FILTERS: InfractionListFilters = {
	search: "",
	status: "all",
	deadlineStartDate: null,
	deadlineEndDate: null,
};

const EMPTY_METRICS: InfractionMetricsSummary = {
	criticalCount: 0,
	awaitingIndicationCount: 0,
	nicRiskAmount: 0,
	pendingTotalAmount: 0,
};

export default function InfractionsPage() {
	const queryClient = useQueryClient();
	const pageRef = useRef<HTMLDivElement>(null);
	const { data: habilitarMeStatus } = useHabilitarMeStatus();
	const { data: userProfile } = useUserProfile();
	const { mutateAsync: markOnboardingSeen } = useMarkInfractionOnboardingSeen();
	const hasDismissedFooter = useInfractionModuleFooterStore(
		(state) => state.hasDismissedFooter
	);
	const _dismissFooter = useInfractionModuleFooterStore(
		(state) => state.dismissFooter
	);

	const hasValidCertificate = habilitarMeStatus?.hasValidCertificate ?? false;
	const showConnectButton = !hasValidCertificate;
	const showContractFooter = !(hasValidCertificate || hasDismissedFooter);

	const [metricFilter, setMetricFilter] = useState<InfractionMetricFilter>(
		INFRACTION_METRIC_FILTER.ALL
	);
	const [filters, setFilters] =
		useState<InfractionListFilters>(DEFAULT_FILTERS);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalInfractionsCount, setTotalInfractionsCount] = useState(0);

	const paginatedParams = useMemo(
		() =>
			buildInfractionPaginatedRequest(
				filters,
				metricFilter,
				currentPage,
				INFRACTION_LIST_PAGE_SIZE
			),
		[filters, metricFilter, currentPage]
	);

	const { data: summaryData, isLoading: isSummaryLoading } =
		useInfractionsSummary();
	const paginatedQuery = useInfractionsPaginated(paginatedParams);
	const {
		data: paginatedData,
		isLoading: isListLoading,
		isFetching: isListFetching,
		isError: isListError,
		refetch: refetchList,
	} = paginatedQuery;

	const { total: totalPages } = usePaginationTotal({
		page: currentPage,
		onPageChange: setCurrentPage,
		criteria: { filters, metricFilter, pageSize: INFRACTION_LIST_PAGE_SIZE },
		totalPages: paginatedData?.totalPages,
		query: paginatedQuery,
	});

	const isFiltering = hasActiveInfractionFilters(filters, metricFilter);

	useEffect(() => {
		if (!(isListLoading || isListFetching || isListError || isFiltering)) {
			setTotalInfractionsCount(paginatedData?.totalCount ?? 0);
		}
	}, [
		isListLoading,
		isListFetching,
		isListError,
		isFiltering,
		paginatedData?.totalCount,
	]);

	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [detailOpen, setDetailOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);
	const [isExportModalOpen, setIsExportModalOpen] = useState(false);
	const [successModal, setSuccessModal] = useState<"create" | "edit" | null>(
		null
	);

	const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
	const [onboardingStartAtConnect, setOnboardingStartAtConnect] =
		useState(false);
	const [onboardingStep, setOnboardingStep] = useState(1);

	const { openHabilitarMeInNewTab, isSigningInHabilitarMe } =
		useInfractionsSignIn();

	const listItems = paginatedData?.items ?? [];
	const metrics = summaryData ?? EMPTY_METRICS;

	const hasInfractions = totalInfractionsCount > 0;
	const showEmptyList = !(
		isListLoading ||
		isListError ||
		hasInfractions ||
		isFiltering
	);

	const invalidateInfractionQueries = () => {
		invalidateInfractionListQueries(queryClient);
	};

	const invalidateInfractionQueriesAfterEdit = () => {
		invalidateInfractionListQueries(queryClient);
		invalidateInfractionDetailQueries(queryClient);
	};

	const openDetail = (id: string) => {
		setSelectedId(id);
		setDetailOpen(true);
	};

	const openEdit = (id: string) => {
		setSelectedId(id);
		setDetailOpen(false);
		setEditOpen(true);
	};

	const openOnboardingFlow = () => {
		const skipSlideshow = userProfile?.hasSeenInfractionOnboarding ?? false;
		setOnboardingStartAtConnect(skipSlideshow);
		if (!skipSlideshow) {
			setOnboardingStep(1);
		}
		setIsOnboardingOpen(true);
	};

	const handleConnectCompany = () => {
		openOnboardingFlow();
	};

	const handleHabilitarMeSignIn = () => {
		openHabilitarMeInNewTab();
	};

	const renderListContent = () => {
		if (showEmptyList) {
			return <InfractionsEmptyState />;
		}

		return (
			<InfractionsTable
				footer={
					totalPages !== null && totalPages > 1 ? (
						<PaginationPageDefault
							className="p-5"
							onPageChange={setCurrentPage}
							page={currentPage}
							total={totalPages}
						/>
					) : null
				}
				isError={isListError}
				isLoading={isListFetching}
				items={isListFetching ? [] : listItems}
				onEdit={openEdit}
				onGenerateGuide={() => {
					toast(
						ToastCustom({
							description: "Geração de guia disponível após integração.",
							title: "Em breve",
						})
					);
				}}
				onRetry={() => {
					const nav = pageRef.current?.querySelector<HTMLElement>(
						"nav[aria-label='Paginação']"
					);
					const target = nav ? findPaginationFocusTarget(nav) : null;
					target?.focus({ preventScroll: true });
					refetchList();
				}}
				onRowClick={openDetail}
			/>
		);
	};

	return (
		<div data-slot="infractions-page" ref={pageRef}>
			<div className="space-y-6 px-6 py-8">
				<InfractionsPageHeader
					isConnecting={isSigningInHabilitarMe}
					{...(showConnectButton
						? { onConnectCompany: handleConnectCompany }
						: {})}
				/>

				<InfractionsMetricsCards
					activeFilter={metricFilter}
					isLoading={isSummaryLoading}
					metrics={metrics}
					onFilterChange={(filter) => {
						setMetricFilter(filter);
						setCurrentPage(1);
					}}
				/>

				<InfractionsFiltersToolbar
					filters={filters}
					onExport={() => {
						setIsExportModalOpen(true);
					}}
					onFiltersChange={setFilters}
					onNewInfraction={() => {
						setCreateOpen(true);
					}}
					onPageChange={setCurrentPage}
				/>

				{renderListContent()}
			</div>

			{showContractFooter
				? // <InfractionsModuleFooter
					// 	onContractModule={openOnboardingFlow}
					// 	onDismiss={dismissFooter}
					// />
					null
				: null}

			<InfractionDetailDrawer
				infractionId={selectedId}
				isOpen={detailOpen}
				onEdit={openEdit}
				onOpenChange={setDetailOpen}
			/>

			<InfractionCreateDrawer
				infractionId={selectedId ?? undefined}
				isOpen={editOpen}
				mode="edit"
				onOpenChange={setEditOpen}
				onSaved={() => {
					invalidateInfractionQueriesAfterEdit();
					setSuccessModal("edit");
				}}
			/>

			<InfractionCreateDrawer
				isOpen={createOpen}
				onCreated={() => {
					invalidateInfractionQueries();
					setSuccessModal("create");
				}}
				onOpenChange={setCreateOpen}
			/>

			<InfractionOnboardingModal
				companyDocument={habilitarMeStatus?.companyDocument ?? null}
				companyName={habilitarMeStatus?.companyName ?? null}
				isOpen={isOnboardingOpen}
				onClose={() => {
					setIsOnboardingOpen(false);
				}}
				onConnectSuccess={() => {
					handleHabilitarMeSignIn();
				}}
				onManualContinue={() => {
					setIsOnboardingOpen(false);
					setCreateOpen(true);
				}}
				onMarkOnboardingSeen={markOnboardingSeen}
				onSkip={() => {
					setIsOnboardingOpen(false);
				}}
				onSlideshowStepChange={setOnboardingStep}
				slideshowStep={onboardingStep}
				startAtConnect={onboardingStartAtConnect}
			/>

			<InfractionSuccessModal
				isOpen={successModal !== null}
				onClose={() => {
					setSuccessModal(null);
				}}
				variant={successModal ?? "create"}
			/>

			<InfractionsExportModal
				isOpen={isExportModalOpen}
				onClose={() => {
					setIsExportModalOpen(false);
				}}
			/>
		</div>
	);
}
